import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DivisionUsersService } from '../services/division-users-service';
import httpClient from '../services/http-client';
import { 
  UserDto, 
  CreateUserRequest, 
  UpdateUserRequest, 
  UserListResponse,
  UserActivityDto,
  BatchCreateUsersRequest,
  BatchCreateUsersResponse,
  UserMatchIdentifier,
  CreateUserMatchIdentifierRequest,
  UpdateUserMatchIdentifierRequest,
  UserMatchIdentifierListResponse,
  BatchMatchIdentifierRequest,
  BatchMatchIdentifierResponse,
  TestUserMatchIdentifierRequest,
  TestUserMatchIdentifierResponse
} from '../types';

// Create a division users service instance using the default HTTP client
const divisionUsersService = new DivisionUsersService(httpClient);

// Query keys
export const divisionUsersQueryKeys = {
  all: ['divisionUsers'] as const,
  lists: () => [...divisionUsersQueryKeys.all, 'list'] as const,
  list: (divisionId: string, filters: Record<string, any>) => [...divisionUsersQueryKeys.lists(), divisionId, filters] as const,
  details: () => [...divisionUsersQueryKeys.all, 'detail'] as const,
  detail: (divisionId: string, userId: string) => [...divisionUsersQueryKeys.details(), divisionId, userId] as const,
  matchIdentifiers: (divisionId: string, userId: string) => [...divisionUsersQueryKeys.all, 'matchIdentifiers', divisionId, userId] as const,
  activity: (divisionId: string, userId: string) => [...divisionUsersQueryKeys.all, 'activity', divisionId, userId] as const,
};

/**
 * Hook to get all users in a division with optional filtering
 */
export function useDivisionUsers(
  divisionId: string,
  params?: {
    active?: boolean;
    search?: string;
    matchIdentifierId?: string;
    matchIdentifierValue?: string;
    hasMatchIdentifiers?: boolean;
    limit?: number;
    offset?: number;
  },
  enabled = true
) {
  return useQuery({
    queryKey: divisionUsersQueryKeys.list(divisionId, params ?? {}),
    queryFn: () => divisionUsersService.getUsers(divisionId, params),
    enabled: enabled && !!divisionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get a specific user by ID
 */
export function useDivisionUser(divisionId: string, userId: string, enabled = true) {
  return useQuery({
    queryKey: divisionUsersQueryKeys.detail(divisionId, userId),
    queryFn: () => divisionUsersService.getUserById(divisionId, userId),
    enabled: enabled && !!divisionId && !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to create a new user in a division
 */
export function useCreateDivisionUser(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData: CreateUserRequest) => 
      divisionUsersService.createUser(divisionId, userData),
    onSuccess: (newUser) => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.lists() 
      });
      
      // Add the new user to the cache
      queryClient.setQueryData(
        divisionUsersQueryKeys.detail(divisionId, newUser.id),
        newUser
      );
    },
  });
}

/**
 * Hook to update an existing user
 */
export function useUpdateDivisionUser(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, userData }: { userId: string; userData: UpdateUserRequest }) => 
      divisionUsersService.updateUser(divisionId, userId, userData),
    onSuccess: (updatedUser, { userId }) => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.lists() 
      });
      
      // Update the user in cache
      queryClient.setQueryData(
        divisionUsersQueryKeys.detail(divisionId, userId),
        updatedUser
      );
    },
  });
}

/**
 * Hook to delete a user
 */
export function useDeleteDivisionUser(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => 
      divisionUsersService.deleteUser(divisionId, userId),
    onSuccess: (_, userId) => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.lists() 
      });
      
      // Remove the user from cache
      queryClient.removeQueries({ 
        queryKey: divisionUsersQueryKeys.detail(divisionId, userId) 
      });
    },
  });
}

/**
 * Hook to reactivate a deactivated user
 */
export function useActivateDivisionUser(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => 
      divisionUsersService.activateUser(divisionId, userId),
    onSuccess: (_, userId) => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.lists() 
      });

      // Invalidate the cached detail so the reactivated status is refetched
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.detail(divisionId, userId) 
      });
    },
  });
}

/**
 * Hook to get user match identifiers
 */
export function useUserMatchIdentifiers(
  divisionId: string, 
  userId: string, 
  enabled = true
) {
  return useQuery({
    queryKey: divisionUsersQueryKeys.matchIdentifiers(divisionId, userId),
    queryFn: () => divisionUsersService.getUserMatchIdentifiers(divisionId, userId),
    enabled: enabled && !!divisionId && !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to assign a match identifier to a user
 */
export function useAssignUserMatchIdentifier(divisionId: string, userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateUserMatchIdentifierRequest) => 
      divisionUsersService.assignMatchIdentifier(divisionId, userId, request),
    onSuccess: () => {
      // Invalidate match identifiers for this user
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.matchIdentifiers(divisionId, userId) 
      });
      
      // Also invalidate user details as they might include match identifier count
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.detail(divisionId, userId) 
      });
    },
  });
}

/**
 * Hook to update a user match identifier
 */
export function useUpdateUserMatchIdentifier(divisionId: string, userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ identifierId, request }: { 
      identifierId: string; 
      request: UpdateUserMatchIdentifierRequest 
    }) => 
      divisionUsersService.updateUserMatchIdentifier(divisionId, userId, identifierId, request),
    onSuccess: () => {
      // Invalidate match identifiers for this user
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.matchIdentifiers(divisionId, userId) 
      });
    },
  });
}

/**
 * Hook to remove a user match identifier
 */
export function useRemoveUserMatchIdentifier(divisionId: string, userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (identifierId: string) => 
      divisionUsersService.removeUserMatchIdentifier(divisionId, userId, identifierId),
    onSuccess: () => {
      // Invalidate match identifiers for this user
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.matchIdentifiers(divisionId, userId) 
      });
      
      // Also invalidate user details
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.detail(divisionId, userId) 
      });
    },
  });
}

/**
 * Hook to batch assign match identifiers to a user
 */
export function useBatchAssignMatchIdentifiers(divisionId: string, userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BatchMatchIdentifierRequest) => 
      divisionUsersService.batchAssignMatchIdentifiers(divisionId, userId, request),
    onSuccess: () => {
      // Invalidate match identifiers for this user
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.matchIdentifiers(divisionId, userId) 
      });
      
      // Also invalidate user details
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.detail(divisionId, userId) 
      });
    },
  });
}

/**
 * Hook to test user match identifiers
 */
export function useTestUserMatchIdentifiers(divisionId: string, userId: string) {
  return useMutation({
    mutationFn: (request: TestUserMatchIdentifierRequest) => 
      divisionUsersService.testUserMatchIdentifiers(divisionId, userId, request),
  });
}

/**
 * Hook to get user activity
 */
export function useUserActivity(
  divisionId: string, 
  userId: string,
  params?: {
    startDate?: string;
    endDate?: string;
  },
  enabled = true
) {
  return useQuery({
    queryKey: divisionUsersQueryKeys.activity(divisionId, userId),
    queryFn: () => divisionUsersService.getUserActivity(divisionId, userId, params),
    enabled: enabled && !!divisionId && !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to batch create users
 */
export function useBatchCreateUsers(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BatchCreateUsersRequest) => 
      divisionUsersService.batchCreateUsers(divisionId, request),
    onSuccess: () => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.lists() 
      });
    },
  });
}

/**
 * Hook to import users into a division
 */
export function useImportDivisionUsers(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      file: File;
      options?: {
        format?: 'csv' | 'json';
        updateExisting?: boolean;
        includeMatchIdentifiers?: boolean;
      };
    }) => 
      divisionUsersService.importDivisionUsers(
        divisionId,
        params.file,
        params.options || {}
      ),
    onSuccess: () => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ 
        queryKey: divisionUsersQueryKeys.lists() 
      });
    },
  });
}

/**
 * Hook to export users from a division
 */
export function useExportDivisionUsers(divisionId: string) {
  return useMutation({
    mutationFn: (options?: {
      format?: 'csv' | 'json';
      active?: boolean;
      includeMatchIdentifiers?: boolean;
    }) => 
      divisionUsersService.exportDivisionUsers(divisionId, options || {}),
  });
}
